import clsx from "clsx"

import { dashIfZero } from "@/lib/format"
import {
  PRIORITY_ROW_ORDER,
  STATE_COLUMN_ORDER,
  type PriorityRowCounts,
} from "@/types/sprint-report"

interface Props {
  matrix: Record<string, PriorityRowCounts>
}

export function PriorityMatrix({ matrix }: Props) {
  return (
    <div className="overflow-x-auto rounded border border-slate-300">
      <table className="matrix-table">
        <thead>
          <tr>
            <th rowSpan={2} className="sticky left-0 z-10 bg-header">Priority</th>
            <th rowSpan={2}>Description</th>
            <th rowSpan={2}>Done in<br/>Previous Sprint</th>
            <th rowSpan={2}>Backlog +<br/>Future Sprints</th>
            <th rowSpan={2} className="bg-headerSub">Sprint Backlog Size<br/>(Current) (A)</th>
            <th colSpan={STATE_COLUMN_ORDER.length} className="bg-headerSub">
              Breakdown within current sprint
            </th>
            <th rowSpan={2}>Remaining<br/>in Sprint (A − Done)</th>
            <th rowSpan={2}>New Addition<br/>Backlog + Future</th>
            <th rowSpan={2}>Remaining<br/>Overall</th>
          </tr>
          <tr>
            {STATE_COLUMN_ORDER.map((col) => (
              <th key={col.key} className="bg-headerSub text-[10px] uppercase tracking-wide">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PRIORITY_ROW_ORDER.map(({ key, label, isSubtotal }) => {
            const row = matrix[key]
            if (!row) return null
            return (
              <tr key={key} className={clsx(isSubtotal && "subtotal")}>
                <td
                  className="row-label"
                  style={{
                    backgroundColor: isSubtotal ? undefined : row.color,
                    color: isSubtotal ? undefined : "white",
                  }}
                >
                  {label}
                </td>
                <td className="text-left text-[11px] text-slate-600">
                  {priorityDescription(key)}
                </td>
                <td>{dashIfZero(row.done_in_previous_sprint)}</td>
                <td className="backlog-cell">{dashIfZero(row.backlog_plus_future)}</td>
                <td className="font-semibold">{dashIfZero(row.current_sprint_size)}</td>
                {STATE_COLUMN_ORDER.map((col) => (
                  <td
                    key={col.key}
                    className={clsx(col.key === "done" && "done-cell")}
                  >
                    {dashIfZero(row.by_state[col.key])}
                  </td>
                ))}
                <td className="remaining-cell font-semibold">
                  {dashIfZero(row.remaining_in_sprint)}
                </td>
                <td>{dashIfZero(row.new_addition)}</td>
                <td className="font-semibold">{dashIfZero(row.remaining_overall)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function priorityDescription(key: string): string {
  switch (key) {
    case "P0":
      return "Critical / Production blocker"
    case "P1":
    case "P1-A":
    case "P1-B":
    case "P1__subtotal":
      return "High Impact: feature broken, critical workflow blocked, no workaround."
    case "P2":
    case "P2-A":
    case "P2-B":
    case "P2__subtotal":
      return "Medium Impact: feature degraded or incorrect info; workaround exists."
    case "P3":
      return "Low Impact: minor UX / cosmetic / non-functional inconsistency."
    default:
      return ""
  }
}
